import { useEffect, useState, type ReactNode } from 'react';
import { UsEntry } from '@/features/us/UsEntry';
import { Tour } from '@/components/fun/Tour';
import { APP_TOUR } from '@/features/tours/tours';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/stores/auth';
import { useMatchPrefs } from '@/stores/matchPrefs';
import { useCatalog, useChats, useConfig, useConnections, useDaily, useDateInvites, useFriendRequests, useInvites, useLinkRequests, useLive } from '@/api/queries';
import type { Avatar as AvatarT, DateLength, Intent, Message, Mood } from '@/api/types';
import { Page } from '@/components/layout/AppShell';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/ui/Avatar';
import { Sheet } from '@/components/ui/Modal';
import { Skeleton } from '@/components/ui/Skeleton';
import { FloatIn, Mascot, PRESS } from '@/components/fun';
import { cn } from '@/lib/cn';
import { FiltersButton, FilterPassChip, isPlus } from '@/features/plus/Filters';
import { useFilterPass } from '@/features/ads/filterPass';
import { preloadFaceDetector } from '@/features/face/detector';
import { useFaceMode } from '@/features/face/store';
import { DailyCard } from '@/features/icebreakers/DailyCard';
import { SponsoredCard } from '@/features/ads/SponsoredCard';
import { FALLBACK_MOODS } from '@/features/profile/MoodPicker';
import { useInviteComposer } from '@/features/invites/composer';
import { UpcomingCard, YourMoveCard, nextDate, yourMove } from '@/features/invites/DatesTile';

export function LiveCount() {
  const live = useLive();
  if (live.isLoading) return <Skeleton className="h-5 w-40" />;
  if (live.isError || !live.data) return <p className="text-sm text-muted">Live counts unavailable right now.</p>;
  const { online_now, searching_now } = live.data;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold" data-testid="live-count" aria-live="polite">
      <span className="inline-flex items-center gap-2">
        <span className="inline-flex h-2.5 w-2.5 rounded-full bg-green" aria-hidden />
        {online_now < 5 ? 'A few people around' : `${online_now.toLocaleString()} online now`}
      </span>
      {online_now >= 5 && searching_now >= 5 && <span className="text-muted">· {searching_now.toLocaleString()} looking</span>}
    </p>
  );
}

// ---- the three doors

const DOOR_TONE = {
  red: 'bg-red text-night',
  night: 'bg-night text-bone',
  yellow: 'bg-yellow text-night',
} as const;

/** A door is a poster field: one flat colour, a loud name, a blob stuck on its edge (docs/DESIGN.md). */
function Door({
  art,
  title,
  line,
  onClick,
  tone,
  testId,
  extra,
  i,
  tour,
}: {
  art: ReactNode;
  title: string;
  line: string;
  onClick: () => void;
  tone: keyof typeof DOOR_TONE;
  testId: string;
  extra?: ReactNode;
  i: number;
  /** v14: the guided tour's anchor. */
  tour?: string;
}) {
  return (
    <FloatIn i={i + 1} className="min-w-0">
      <motion.button
        type="button"
        {...PRESS}
        onClick={onClick}
        className={cn(
          'relative flex min-h-[132px] w-full flex-col justify-end overflow-hidden rounded-card p-5 pr-28 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg lg:min-h-[240px] lg:p-6 lg:pr-6',
          DOOR_TONE[tone],
        )}
        data-testid={testId}
        data-tour={tour}
      >
        <span aria-hidden className="pointer-events-none absolute -right-3 top-1/2 -translate-y-1/2 rotate-[6deg] lg:right-5 lg:top-5 lg:translate-y-0">
          {art}
        </span>
        <span className="relative block font-display text-[34px] leading-[0.9] lg:text-[44px]">{title}</span>
        <span className="relative mt-1.5 flex items-center gap-1 text-[15px] font-medium opacity-80">
          {line}
          <Icon name="chevron-right" size={18} strokeWidth={2.2} />
        </span>
        {extra && <span className="relative">{extra}</span>}
      </motion.button>
    </FloatIn>
  );
}

const DOOR_PAIR: [AvatarT, AvatarT] = [
  { hue: 210, shape: 'flower', face: 'grin' },
  { hue: 330, shape: 'blob', face: 'wink' },
];
const DOOR_GUEST: AvatarT = { hue: 130, shape: 'star', face: 'smile' };

/** Two blobs leaning in: the date door's sticker. */
function DoorPair() {
  return (
    <span className="flex items-end">
      <Avatar avatar={DOOR_PAIR[0]} size={84} sticker />
      <span className="-ml-6 -rotate-12">
        <Avatar avatar={DOOR_PAIR[1]} size={72} sticker />
      </span>
    </span>
  );
}

// ---- Meet someone new (sheet)

function MeetSheet({ open, onClose, onGo }: { open: boolean; onClose: () => void; onGo: (mood: Mood, intent: Intent) => void }) {
  const me = useAuth((s) => s.me);
  const catalog = useCatalog();
  const { mood, setMood } = useMatchPrefs();
  const current = mood ?? me?.default_mood ?? 'chill';
  const moods = catalog.data?.moods?.length ? catalog.data.moods : FALLBACK_MOODS;
  return (
    <Sheet open={open} onClose={onClose} title="Meet someone new" description="1:1 video, an opener ready. Skip anytime." testId="meet-sheet">
      <p className="mb-2 text-sm font-medium">I’m feeling…</p>
      <div role="radiogroup" aria-label="Mood" className="grid grid-cols-2 gap-2">
        {moods.map((m) => {
          const on = current === m.id;
          return (
            <motion.button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={on}
              data-testid={`mood-${m.id}`}
              onClick={() => setMood(m.id as Mood)}
              {...PRESS}
              className={cn(
                'flex h-14 items-center rounded-full px-5 text-[15px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg',
                on ? 'bg-primary text-ground' : 'bg-raised hover:bg-line',
              )}
            >
              {m.label}
            </motion.button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <FiltersButton tone="onDark" />
        <span className="text-xs text-muted">Who you’d like to meet</span>
      </div>
      <motion.button
        type="button"
        {...PRESS}
        onClick={() => onGo(current, 'talk')}
        data-testid="find-someone"
        className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-primary px-6 text-[17px] font-semibold text-ground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg"
      >
        Find someone
        <Icon name="chevron-right" size={20} strokeWidth={2.2} />
      </motion.button>
      <button
        type="button"
        onClick={() => onGo(current, 'play')}
        className="mt-2 flex h-12 w-full items-center justify-center rounded-full text-[15px] font-medium text-muted hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
        data-testid="find-player"
      >
        Or find a game buddy
      </button>
    </Sheet>
  );
}

// ---- Go on a date (sheet)

const DATE_LENGTHS: { len: DateLength; line: string }[] = [
  { len: 30, line: 'Warm-up, a game, one deep question.' },
  { len: 60, line: 'Stories, two games, big dreams.' },
];

function DateSheet({ open, onClose, onGo }: { open: boolean; onClose: () => void; onGo: (len: DateLength) => void }) {
  const { dateLength, setDateLength } = useMatchPrefs();
  const len: DateLength = dateLength === 60 ? 60 : 30;
  return (
    <Sheet open={open} onClose={onClose} title="Go on a date" description="We guide the chat. Skip anything, anytime." testId="date-sheet">
      <div role="radiogroup" aria-label="Date length" className="grid grid-cols-2 gap-2">
        {DATE_LENGTHS.map((d) => (
          <motion.button
            key={d.len}
            type="button"
            role="radio"
            aria-checked={len === d.len}
            onClick={() => setDateLength(d.len)}
            {...PRESS}
            className={cn(
              'rounded-card p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg',
              len === d.len ? 'bg-night text-bone' : 'bg-raised hover:bg-line',
            )}
            data-testid={`date-length-${d.len}`}
          >
            <span className="block font-display text-[40px] tabular-nums leading-none">
              {d.len}
              <span className="ml-1 text-xl">min</span>
            </span>
            <span className={cn('mt-1.5 block text-sm', len === d.len ? 'text-bone/75' : 'text-muted')}>{d.line}</span>
          </motion.button>
        ))}
      </div>
      <motion.button
        type="button"
        {...PRESS}
        onClick={() => onGo(len)}
        className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-primary px-6 text-[17px] font-semibold text-ground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg"
        data-testid="date-start"
      >
        Find a date
        <Icon name="chevron-right" size={20} strokeWidth={2.2} />
      </motion.button>
      <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-muted">
        <Icon name="shield" size={13} className="mt-0.5 shrink-0" />
        Only people who picked a date too. Never recorded.
      </p>
    </Sheet>
  );
}

// ---- the one "next thing"

function textPreview(m: Message | null): string | undefined {
  if (!m || m.deleted || (m.kind && m.kind !== 'text')) return undefined;
  return m.body.length > 60 ? `${m.body.slice(0, 57)}…` : m.body;
}

const DAILY_HIDE_KEY = 'popin.daily.hidden';
function dailyHidden(date: string | undefined): boolean {
  if (!date) return false;
  try {
    return localStorage.getItem(DAILY_HIDE_KEY) === date;
  } catch {
    return false;
  }
}

function NextCard({ kicker, kind, children }: { kicker: string; kind: string; children: ReactNode }) {
  return (
    <section aria-label={kicker} data-testid="next-thing" data-kind={kind}>
      <h2 className="mb-2 text-[17px] font-semibold">{kicker}</h2>
      {children}
    </section>
  );
}

function RowLink({ to, avatar, title, sub, testId }: { to: string; avatar?: Parameters<typeof Avatar>[0]['avatar']; title: string; sub?: string; accent?: 'sky' | 'zest' | 'volt'; testId: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 rounded-card bg-surface p-3.5 transition-colors hover:bg-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg" data-testid={testId}>
      {avatar !== undefined && <Avatar avatar={avatar} size={44} />}
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-snug">{title}</span>
        {sub && <span className="block truncate text-sm text-muted">{sub}</span>}
      </span>
      <Icon name="chevron-right" size={18} />
    </Link>
  );
}

/**
 * At most ONE card below the doors (SPEC_V10 Home), by priority: a date on
 * soon (or running late) → a date or request waiting on me → messages,
 * calls, friend requests → today's question → a live Drop.
 */
export function NextThing() {
  const config = useConfig();
  const datesOn = config.data?.features.date_invites !== false;
  const linksOn = datesOn && config.data?.features.date_links !== false;
  const invites = useDateInvites(datesOn);
  const linkReqs = useLinkRequests(linksOn);
  const chats = useChats();
  const friendReqs = useFriendRequests();
  const calls = useInvites();
  const daily = useDaily();
  const live = useLive();

  if ((datesOn && invites.isLoading) || chats.isLoading || daily.isLoading) {
    return <Skeleton className="h-24 w-full" rounded="rounded-sheet" />;
  }
  const list = invites.data?.invites ?? [];
  const soon = nextDate(list);
  if (soon)
    return (
      <NextCard kicker="Coming up" kind="date">
        <UpcomingCard inv={soon} />
      </NextCard>
    );
  const move = yourMove(list);
  if (move)
    return (
      <NextCard kicker="Your move" kind="your-move">
        <YourMoveCard inv={move} />
      </NextCard>
    );
  const req = linkReqs.data?.incoming.find((r) => r.status === 'pending');
  if (req)
    return (
      <NextCard kicker="Date request" kind="link-request">
        <RowLink to="/requests" avatar={req.requester.avatar} title={`${req.requester.display_name} asked you out`} sub="Swipe to answer. Only you decide." accent="zest" testId="next-link-request" />
      </NextCard>
    );
  const call = calls.data?.incoming[0];
  if (call)
    return (
      <NextCard kicker="Calling you" kind="call">
        <RowLink to="/friends" avatar={call.from.avatar} title={`${call.from.display_name} wants to call`} sub="Open Friends to pick up." testId="next-call" />
      </NextCard>
    );
  const unread = (chats.data?.chats ?? []).filter((c) => c.unread_count > 0);
  if (unread.length) {
    const n = unread.reduce((s, c) => s + c.unread_count, 0);
    const first = unread[0]!;
    return (
      <NextCard kicker="New messages" kind="messages">
        <RowLink
          to={unread.length === 1 ? `/friends/${first.connection_id}/chat` : '/chats'}
          avatar={first.friend.avatar}
          title={unread.length === 1 ? `${first.friend.display_name} sent you ${n === 1 ? 'a message' : `${n} messages`}` : `${n} new messages`}
          sub={unread.length === 1 ? textPreview(first.last_message) : unread.map((c) => c.friend.display_name).join(', ')}
          testId="next-messages"
        />
      </NextCard>
    );
  }
  const fr = friendReqs.data?.incoming ?? [];
  if (fr.length)
    return (
      <NextCard kicker="Friend request" kind="friend-request">
        <RowLink to="/friends" avatar={fr[0]!.from.avatar} title={fr.length === 1 ? `${fr[0]!.from.display_name} wants to be friends` : `${fr.length} friend requests`} sub="Say hi back?" testId="next-friend-request" />
      </NextCard>
    );
  const d = daily.data;
  if (d && !d.my_answer && !dailyHidden(d.date)) return <DailyCard />;
  const drop = config.data?.features.drops ? live.data?.live_drop : null;
  if (drop)
    return (
      <NextCard kicker="Live now" kind="drop">
        <RowLink to={`/drops/${drop.id}`} title={drop.title} sub={drop.tagline} accent="volt" testId="next-drop" />
      </NextCard>
    );
  if (d && !dailyHidden(d.date)) return <DailyCard />;
  return null;
}

// ---- friends online

function FriendsOnline() {
  const conns = useConnections();
  const online = (conns.data?.connections ?? []).filter((c) => c.online);
  if (!online.length) return null;
  return (
    <Link
      to="/friends"
      className="flex items-center gap-3 rounded-full bg-surface py-2 pl-2 pr-4 transition-colors hover:bg-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
      data-testid="friends-online"
      aria-label={`${online.length} ${online.length === 1 ? 'friend' : 'friends'} around now. Open Friends`}
    >
      <span className="flex -space-x-2">
        {online.slice(0, 5).map((c) => (
          <span key={c.id} className="rounded-full border-2 border-surface">
            <Avatar avatar={c.user.avatar} size={32} />
          </span>
        ))}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
        {online.length === 1 ? `${online[0]!.user.display_name} is around` : `${online.length} friends around`}
      </span>
      <Icon name="chevron-right" size={16} className="text-fg" />
    </Link>
  );
}

export function Home() {
  const me = useAuth((s) => s.me);
  const navigate = useNavigate();
  const config = useConfig();
  const { mood: savedMood, setMood, setIntent, setDateLength } = useMatchPrefs();
  const startInvite = useInviteComposer((s) => s.start);
  const pass = useFilterPass();
  const faceMode = useFaceMode();
  const [sheet, setSheet] = useState<'meet' | 'date' | null>(null);
  // Warm the on-device face detector so the pre-match check is instant.
  useEffect(() => {
    if (faceMode === 'required') preloadFaceDetector();
  }, [faceMode]);
  const go = (mood: Mood, intent: Intent) => {
    setMood(mood);
    setIntent(intent);
    setSheet(null);
    navigate('/match', { state: { mood, intent } });
  };
  /** v6: Date Night queue (`intent:'date'` + `date_length_min`). */
  const goDate = (len: DateLength) => {
    const mood = savedMood ?? me?.default_mood ?? 'chill';
    setMood(mood);
    setIntent('date');
    setDateLength(len);
    setSheet(null);
    navigate('/match', { state: { mood, intent: 'date', dateLength: len } });
  };
  const invitesOn = config.data?.features.date_invites !== false;
  const matchingOn = config.data?.features.open_matching !== false;
  const doors = (matchingOn ? 2 : 0) + (invitesOn ? 1 : 0);
  const passOn = !!me && !isPlus(me) && pass.active;
  return (
    <Page className="overflow-x-clip">
      <header className="mb-5 lg:mb-8">
        <FloatIn className="flex items-center gap-3">
          <Mascot avatar={me?.avatar} size={52} mood="wave" />
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold">Hey {me?.display_name}</p>
            <LiveCount />
          </div>
        </FloatIn>
        <FloatIn i={1}>
          <h1 className="mt-5 font-display text-[48px] leading-[0.9] sm:text-[72px]">What are you up for?</h1>
        </FloatIn>
      </header>
      <div className={cn('grid gap-3 lg:gap-4', doors === 3 ? 'lg:grid-cols-3' : doors === 2 ? 'lg:grid-cols-2' : '')}>
        {matchingOn && (<>
        <Door
          i={0}
          art={<Avatar avatar={me?.avatar} size={104} sticker />}
          title="Meet someone new"
          line="Live video, right now"
          tone="red"
          onClick={() => setSheet('meet')}
          testId="door-meet"
          tour="door-meet"
          extra={passOn ? <span className="mt-2 block"><FilterPassChip remainingMs={pass.remainingMs} tone="onPop" /></span> : undefined}
        />
        <Door i={1} art={<DoorPair />} title="Go on a date" line="A guided date, 30 or 60 min" tone="night" onClick={() => setSheet('date')} testId="door-date" tour="door-date" />
        </>)}
        {invitesOn && <Door i={2} art={<Avatar avatar={DOOR_GUEST} size={96} sticker />} title="Invite someone" line="Send a link, meet on video" tone="yellow" onClick={() => startInvite({ source: 'home' })} testId="dates-invite-someone" tour="door-invite" />}
      </div>
      {/* v15: the couple space (docs/SPEC_V15_US.md). */}
      <FloatIn i={3} className="mt-3 lg:mt-4">
        <UsEntry />
      </FloatIn>
      <div className="mt-5 grid gap-3 lg:mt-6 lg:grid-cols-[2fr_1fr] lg:items-start lg:gap-4">
        <FloatIn i={4} className="min-w-0">
          <NextThing />
        </FloatIn>
        <FloatIn i={5} className="min-w-0 lg:pt-6">
          <FriendsOnline />
        </FloatIn>
      </div>
      {/* SPEC_V5 §4: one clearly labelled Sponsored card, below the main tiles — never above Quick Meet. */}
      <SponsoredCard placement="home_card" className="mt-4 lg:max-w-[calc((100%-2rem)/3)]" />
      <MeetSheet open={sheet === 'meet'} onClose={() => setSheet(null)} onGo={go} />
      <DateSheet open={sheet === 'date'} onClose={() => setSheet(null)} onGo={goDate} />
      {/* v14: the first time here, a short tour of what's what. */}
      <Tour id="app" steps={APP_TOUR} when={!!me?.onboarded && sheet === null} />
    </Page>
  );
}
